// external libraries
import { debounce } from 'lodash-es';

// constants
import {
  LIVE_UPDATE_ERROR_GAP_MS,
  LIVE_UPDATE_MAX_WAIT_MS,
  LIVE_UPDATE_MIN_GAP_MS,
  LIVE_UPDATE_QUIET_MS
} from '../RosterPlan.consts';

// types
import type { QueryClient } from 'react-query';

export type CoalescedInvalidator = {
  /** Marks a query as changed; it is invalidated once the burst has passed. */
  add: (queryKey: string) => void;
  /** Drops everything pending, for a screen that is going away. */
  cancel: () => void;
};

/**
 * Collects query keys over a burst of messages and invalidates each once, see
 * `LIVE_UPDATE_QUIET_MS`.
 *
 * A query that is still fetching when the burst ends stays pending and is retried after the next
 * quiet period, rather than joined or cancelled. Joining would lose the change: the fetch may predate
 * it, and react-query marks its answer fresh. Cancelling would starve the grid under a steady
 * stream: a month fetch slower than the maximum wait would be restarted before it ever lands, while
 * the abandoned request still runs in full on the server. Waiting keeps it to one fetch in flight
 * per grid, followed by exactly one more that is sure to include the change.
 *
 * A query invalidated less than `LIVE_UPDATE_MIN_GAP_MS` ago stays pending the same way, so a long
 * stream refetches a grid every ten seconds rather than back to back.
 *
 * A query whose last load failed waits `LIVE_UPDATE_ERROR_GAP_MS` from that failure instead. It is
 * still retried, so a grid that failed during a deploy recovers by itself, but tenant-wide traffic
 * cannot turn it into a failing month GET, with an error snackbar, every ten seconds.
 */
export const createCoalescedInvalidator = (
  queryClient: QueryClient
): CoalescedInvalidator => {
  const pendingQueryKeys = new Set<string>();
  const lastInvalidatedAt = new Map<string, number>();

  /**
   * When the query's last load failed, `null` when it did not. A query that had data keeps
   * `status: 'error'` while it is refetched; `isReady` sees that fetch first, so it waits like any
   * other.
   */
  const getLastFailedAt = (queryKey: string) => {
    const failedAt = queryClient
      .getQueryCache()
      .findAll([queryKey])
      .filter((query) => query.state.status === 'error')
      .map((query) => query.state.errorUpdatedAt);
    return failedAt.length > 0 ? Math.max(...failedAt) : null;
  };

  const isReady = (queryKey: string) => {
    if (queryClient.isFetching([queryKey]) > 0) return false;

    const lastFailedAt = getLastFailedAt(queryKey);
    if (lastFailedAt !== null) {
      return Date.now() - lastFailedAt >= LIVE_UPDATE_ERROR_GAP_MS;
    }
    return (
      Date.now() - (lastInvalidatedAt.get(queryKey) ?? -Infinity) >=
      LIVE_UPDATE_MIN_GAP_MS
    );
  };

  const invalidatePending = debounce(
    () => {
      pendingQueryKeys.forEach((queryKey) => {
        if (!isReady(queryKey)) return;
        queryClient.invalidateQueries([queryKey]);
        lastInvalidatedAt.set(queryKey, Date.now());
        pendingQueryKeys.delete(queryKey);
      });
      if (pendingQueryKeys.size > 0) invalidatePending();
    },
    LIVE_UPDATE_QUIET_MS,
    { maxWait: LIVE_UPDATE_MAX_WAIT_MS }
  );

  return {
    add: (queryKey) => {
      pendingQueryKeys.add(queryKey);
      invalidatePending();
    },
    cancel: () => {
      invalidatePending.cancel();
      pendingQueryKeys.clear();
    }
  };
};
